import Document from "~/models/read/Document";
import Student from "~/models/read/User/Student";
import Invoice from "~/models/read/Invoice";
import Service from "~/models/read/Service";

const STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
}

export default class AIProofreaderOrder {
  private readonly _id: any;
  private _status: string;
  private readonly _document: Document;
  private readonly _paidAt: Date;
  private readonly _completedAt: Date|null;
  private readonly _services: Array<Service>;
  private readonly _student: Student;
  private readonly _markedAsHasNoCitations: boolean;
  private readonly _freePlagiarismCheckWithOrder: boolean;
  private readonly _hasValidFreePlagiarismCheckVoucher: boolean;
  private readonly _paraphraserMode: string;
  private readonly _links: object;
  private _invoices: Invoice[];

  constructor({
    id,
    status,
    document,
    paidAt,
    completedAt,
    services,
    student,
    markedAsHasNoCitations,
    freePlagiarismCheckWithOrder,
    hasValidFreePlagiarismCheckVoucher,
    links,
    paraphraserMode,
  }) {
    this._id = id;
    this._status = status;
    this._document = new Document(document);
    this._completedAt = completedAt ? new Date(completedAt) : null;
    this._paidAt = new Date(paidAt);
    this._services = services ? services.map(s => new Service(s)) : null;
    this._student = new Student(student);
    this._markedAsHasNoCitations = markedAsHasNoCitations;
    this._freePlagiarismCheckWithOrder = freePlagiarismCheckWithOrder;
    this._hasValidFreePlagiarismCheckVoucher = hasValidFreePlagiarismCheckVoucher;
    this._links = links;
    this._invoices = [];
    this._paraphraserMode = paraphraserMode;
  }

  get id(): any {
    return this._id;
  }

  get status(): string {
    return this._status;
  }

  get document(): Document {
    return this._document;
  }

  get paidAt(): Date {
    return this._paidAt;
  }

  get completedAt(): Date | null {
    return this._completedAt;
  }

  get isCompleted() : boolean {
      return this._status === STATUS.COMPLETED;
  }

  get isProcessing() : boolean {
      return !(this.isCompleted || this.isFailed);
  }

  get isFailed() : boolean {
    let diff: number = this._paidAt.getTime() - new Date().getTime();
    let totalSeconds = Math.floor(diff / 1000);
    let totalMinutes = Math.floor(totalSeconds / 60);

    if (Math.abs(totalMinutes) > 60) {
      return this._status === STATUS.FAILED;
    }

    return false;
  }

  get services(): Array<Service> {
    return this._services;
  }

  get markedAsHasNoCitations(): boolean {
    return this._markedAsHasNoCitations;
  }

  get student(): Student {
    return this._student;
  }

  get links(): object {
    return this._links;
  }

  get freePlagiarismCheckWithOrder(): boolean {
    return this._freePlagiarismCheckWithOrder;
  }

  get hasValidFreePlagiarismCheckVoucher(): boolean {
    return this._hasValidFreePlagiarismCheckVoucher;
  }

  get isHumanizerMode(): boolean {
    return this._paraphraserMode === 'humanizer';
  }

  addInvoices(invoices) {
    this._invoices = invoices;
  }

  get additionalPaymentInvoices() {
    let invoices = this._invoices.filter(i => i.isOpen() && i.total.isPositive());
    if (invoices.length) {
        this._status = 'additional_payment';
    }
    return invoices;
  }

  get refundInvoices() {
    return this._invoices.filter(i => i.isOpen() && i.total.isNegative());
  }
}
